import {
  LIVE_APPEND_BUDGET_TOKENS,
  LIVE_INPUT_BUDGET_TOKENS,
  type LiveHistoryTurn,
  trimLiveHistory,
} from "./live";
import { partsText } from "./live-call";
import { toSpeakableText } from "./magnus-chat-tool";
import {
  estimateTokens,
  keepNewestWithin,
  type TokenCounter,
  trimToTokens,
} from "./tokens";

const speakerOf = (role: "user" | "assistant") =>
  role === "user" ? "User" : "Magnus";

export function digestLiveTurns(
  turns: readonly LiveHistoryTurn[],
  maxTokens: number,
  count: TokenCounter = estimateTokens
): string {
  const lines = turns
    .map((turn) => `${speakerOf(turn.role)}: ${turn.text.trim()}`)
    .filter((line) => !line.endsWith(": "));
  const kept = keepNewestWithin(lines, maxTokens, (line) => count(line) + 1);
  if (kept.length === lines.length) {
    return kept.join("\n");
  }
  const newest = lines.at(-1);
  const head =
    kept.length === 0 && newest
      ? [trimToTokens(newest, maxTokens, count)]
      : ["…", ...kept];
  return head.join("\n");
}

export const LIVE_SWAP_DIGEST_BUDGET_TOKENS = 3000;

export function splitSwapHistory(
  turns: readonly LiveHistoryTurn[],
  opts: {
    count?: TokenCounter;
    inputTokens?: number;
    digestTokens?: number;
    reserveTokens?: number;
  } = {}
): { recent: LiveHistoryTurn[]; digest: string } {
  const count = opts.count ?? estimateTokens;
  const recent = trimLiveHistory(turns, {
    count,
    maxTokens: opts.inputTokens ?? LIVE_INPUT_BUDGET_TOKENS,
    reserveTokens: opts.reserveTokens,
  });
  const earlier = turns
    .map((turn) => ({ role: turn.role, text: turn.text.trim() }))
    .filter((turn) => turn.text)
    .slice(0, -recent.length || undefined);
  return {
    recent,
    digest: digestLiveTurns(
      earlier,
      opts.digestTokens ?? LIVE_SWAP_DIGEST_BUDGET_TOKENS,
      count
    ),
  };
}

export type ChatRelayFile = { name: string; mediaType?: string };

export type ChatRelayExchange = {
  text: string;
  files: ChatRelayFile[];
  reply: string;
};

type RelayMessage = {
  id: string;
  role: string;
  parts: readonly unknown[];
  metadata?: { voiceSessionId?: string } | null;
};

function partFiles(parts: readonly unknown[]): ChatRelayFile[] {
  const files: ChatRelayFile[] = [];
  for (const part of parts) {
    const p = part as {
      type?: unknown;
      filename?: unknown;
      mediaType?: unknown;
    };
    if (p.type === "file") {
      files.push({
        name: typeof p.filename === "string" ? p.filename : "a file",
        ...(typeof p.mediaType === "string" ? { mediaType: p.mediaType } : {}),
      });
    }
  }
  return files;
}

/**
 * Chat turns the user typed (not spoken through the call) whose reply has
 * landed. `seen` holds user message ids already relayed or present before
 * the call and grows with what this returns; the newest typed turn waits
 * until the engine has settled.
 */
export function collectTypedExchanges(
  messages: readonly RelayMessage[],
  seen: Set<string>,
  settled: boolean
): ChatRelayExchange[] {
  const exchanges: ChatRelayExchange[] = [];
  const lastUser = messages.findLastIndex((m) => m.role === "user");
  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];
    if (
      !message ||
      message.role !== "user" ||
      seen.has(message.id) ||
      message.metadata?.voiceSessionId ||
      (i === lastUser && !settled)
    ) {
      continue;
    }
    seen.add(message.id);
    const text = partsText(message.parts);
    const files = partFiles(message.parts);
    if (!(text || files.length)) {
      continue;
    }
    let reply = "";
    for (let j = i + 1; j < messages.length; j++) {
      const m = messages[j];
      if (!m || m.role === "user") {
        break;
      }
      if (m.role === "assistant") {
        reply = partsText(m.parts) || reply;
      }
    }
    exchanges.push({ text, files, reply });
  }
  return exchanges;
}

const RELAY_REPLY_MAX_TOKENS = 320;
const RELAY_TEXT_MAX_TOKENS = 120;

function describeFiles(files: readonly ChatRelayFile[]): string {
  return files
    .map((file) => {
      const kind = file.mediaType?.split("/")[1]?.toUpperCase();
      return kind ? `${file.name} (${kind})` : file.name;
    })
    .join(", ");
}

export function chatRelayNotes(
  exchange: ChatRelayExchange,
  count: TokenCounter = estimateTokens
): string[] {
  const sent: string[] = [];
  if (exchange.text) {
    sent.push(
      `typed in the chat: "${trimToTokens(exchange.text, RELAY_TEXT_MAX_TOKENS, count)}"`
    );
  }
  if (exchange.files.length) {
    sent.push(`attached ${describeFiles(exchange.files)}`);
  }
  if (!sent.length) {
    return [];
  }
  const lead = `While on this call the user ${sent.join(" and ")}.`;
  const reply = exchange.reply
    ? trimToTokens(
        toSpeakableText(exchange.reply, 4000),
        RELAY_REPLY_MAX_TOKENS,
        count
      )
    : "";
  const tail =
    "They can read the full reply on screen. Bring it up only if it's relevant or they ask; don't read it aloud.";
  const note = reply
    ? `${lead} Magnus replied in the chat: ${reply} ${tail}`
    : `${lead} The chat hasn't replied yet. ${tail}`;
  return [trimToTokens(note, LIVE_APPEND_BUDGET_TOKENS, count)];
}
